import { Asset, MoveAssetResponse } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { ROOT_FOLDER_ID } from '@/utils/folders';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from '../folders/useFetchFolderContents';
import { GET_ASSET_QUERY_KEY } from './useGetAsset';

export const useMoveAsset = (options?: {
  onSuccess?: (movedAsset: Asset) => void;
  onError?: (error: string) => void;
}) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();

  const { mutate, isPending, error } = useMutation<
    AxiosResponse<MoveAssetResponse>,
    AxiosError<{ message: string }>,
    { asset: Asset; folderId: string | undefined }
  >({
    mutationKey: ['moveAsset'],
    mutationFn: ({ asset, folderId }: { asset: Asset; folderId: string | undefined }) =>
      api.assets.moveAsset(tenantId, asset.id, {
        folderId: folderId ?? ROOT_FOLDER_ID,
      }),
    onSuccess: data => {
      options?.onSuccess?.(data.data.asset);
      queryClient.resetQueries({
        queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY],
        exact: false,
      });
      queryClient.resetQueries({
        queryKey: [GET_ASSET_QUERY_KEY],
        exact: false,
      });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      options?.onError?.(error.response?.data.message ?? 'Failed to move asset');
    },
  });

  return {
    moveAsset: mutate,
    isMoving: isPending,
    error,
  };
};
